/**
 * Breakdowns (Status, Services, AE, Geographic) + Donut Charts + Comparison.
 */
import React from 'react';
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer 
} from 'recharts';
import { TrendingUp, PieChart as PieChartIcon } from 'lucide-react';
import { StatusBar } from './analyticsHelpers.jsx';
import { ChartWithDrillDown, commonColumns } from './DrillDownHelpers';

const DONUT_COLORS = ['#4f46e5', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

export default function BreakdownCharts({ 
  analytics, summary, status_breakdown, service_breakdown, 
  account_executive_breakdown, state_breakdown,
  donutChartData, donutChartView, setDonutChartView,
  handleDrillDown 
}) {
  if (!analytics) return null;
  
  // Prepare drill-down data for service breakdown
  const serviceData = Object.entries(service_breakdown).map(([service, data]) => ({
    service_name: service,
    count: data.count,
    gross_revenue: data.gross_revenue,
    net_revenue: data.net_revenue,
  }));
  
  // Prepare drill-down data for account executive breakdown  
  const aeData = Object.entries(account_executive_breakdown).map(([name, data]) => ({
    ae_name: name,
    proposals: data.count,
    approved_proposals: data.approved,
    pending_proposals: data.pending,
    rejected_proposals: data.rejected,
    total_value: (analytics.detailed_scenarios || [])
      .filter(s => s.creator_name === name)
      .reduce((sum, s) => sum + (s.total_net_revenue || 0), 0)
  }));
  
  // Prepare drill-down data for state breakdown
  const stateData = Object.entries(state_breakdown).map(([state, count]) => ({
    state,
    proposal_count: count,
    total_revenue: (analytics.detailed_scenarios || [])
      .filter(s => s.municipality?.includes(state))
      .reduce((sum, s) => sum + (s.total_net_revenue || 0), 0)
  }));

  // Column definitions for drill-downs
  const serviceColumns = [
    { key: 'service_name', label: 'Service', sortable: true },
    { key: 'count', label: 'Scenario Count', sortable: true },
    { key: 'gross_revenue', label: 'Gross Revenue', sortable: true, render: (v) => `$${(v / 1000).toFixed(1)}K` },
    { key: 'net_revenue', label: 'Net Revenue', sortable: true, render: (v) => `$${(v / 1000).toFixed(1)}K` },
  ];
  
  const aeColumns = [
    { key: 'ae_name', label: 'Account Executive', sortable: true },
    { key: 'proposals', label: 'Total Proposals', sortable: true },
    { key: 'approved_proposals', label: 'Approved', sortable: true },
    { key: 'pending_proposals', label: 'Pending', sortable: true },
    { key: 'rejected_proposals', label: 'Rejected', sortable: true },
    { key: 'total_value', label: 'Total Value', sortable: true, render: (v) => `$${(v / 1000).toFixed(1)}K` },
  ];
  
  const stateColumns = [
    { key: 'state', label: 'State', sortable: true },
    { key: 'proposal_count', label: 'Proposals', sortable: true },
    { key: 'total_revenue', label: 'Total Revenue', sortable: true, render: (v) => `$${(v / 1000).toFixed(1)}K` },
  ];

  return (
    <>
      {/* Comparison Metrics */}
      {analytics.comparison_data && Object.keys(analytics.comparison_data).length > 0 && (
        <div className="glass-card p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            {analytics.comparison_data.period_label} Comparison
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { label: 'Total Scenarios', value: analytics.comparison_data.current_total, change: analytics.comparison_data.change_count, pct: analytics.comparison_data.change_percent, prev: analytics.comparison_data.comparison_total, format: 'num' },
              { label: 'Gross Revenue', value: analytics.comparison_data.current_gross_revenue, change: analytics.comparison_data.revenue_change, pct: analytics.comparison_data.revenue_change_percent, prev: analytics.comparison_data.comparison_gross_revenue, format: 'K' },
              { label: 'Approved', value: analytics.comparison_data.current_approved, change: analytics.comparison_data.approval_change, prev: analytics.comparison_data.comparison_approved, format: 'num' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <p className="text-sm text-slate-600 mb-1">{item.label}</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl font-bold text-slate-900">
                    {item.format === 'K' ? `$${(item.value / 1000).toFixed(1)}K` : item.value}
                  </span>
                  <div className={`flex items-center gap-1 text-sm font-medium ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.change >= 0 ? '↑' : '↓'}
                    {item.format === 'K' ? `$${Math.abs(item.change / 1000).toFixed(1)}K` : Math.abs(item.change)}
                    {item.pct !== undefined && <span className="text-xs">({item.pct?.toFixed(1)}%)</span>}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  vs {item.format === 'K' ? `$${(item.prev / 1000).toFixed(1)}K` : item.prev} in comparison period
                </p>
              </div>
            ))}
            <div className="text-center">
              <p className="text-sm text-slate-600 mb-1">Growth Trend</p>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                analytics.comparison_data.change_percent >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {analytics.comparison_data.change_percent >= 0 ? 'Growing' : 'Declining'}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Revenue velocity: {analytics.comparison_data.revenue_change_percent >= 0 ? '+' : ''}
                {analytics.comparison_data.revenue_change_percent?.toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Charts and Breakdowns Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Status Breakdown */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Status Breakdown</h3>
          <div className="space-y-3">
            <StatusBar label="Draft" count={status_breakdown.draft} total={summary.total_scenarios} color="bg-slate-400" onClick={() => handleDrillDown('status', 'draft')} />
            <StatusBar label="Pending Approval" count={(status_breakdown.pending_approval || 0) + (status_breakdown.pending_cfo_approval || 0) + (status_breakdown.pending_ceo_approval || 0)} total={summary.total_scenarios} color="bg-yellow-500" onClick={() => handleDrillDown('status', 'pending_approval')} />
            <StatusBar label="Approved" count={status_breakdown.approved} total={summary.total_scenarios} color="bg-green-500" onClick={() => handleDrillDown('status', 'approved')} />
            <StatusBar label="Rejected" count={status_breakdown.rejected} total={summary.total_scenarios} color="bg-red-500" onClick={() => handleDrillDown('status', 'rejected')} />
          </div>
        </div>

        {/* Top Services */}
        <ChartWithDrillDown
          title="Top Services"
          subtitle="Service performance and revenue breakdown"
          data={serviceData}
          columns={serviceColumns}
        >
          <div className="space-y-3">
            {Object.entries(service_breakdown).sort(([, a], [, b]) => b.count - a.count).slice(0, 5).map(([service, data]) => (
              <div key={service} className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">{service}</span>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-600">{data.count} scenarios</span>
                  <span className="text-sm font-semibold text-green-600">${(data.net_revenue / 1000).toFixed(1)}K</span>
                </div>
              </div>
            ))}
            {Object.keys(service_breakdown).length === 0 && <p className="text-sm text-slate-500 text-center py-4">No service data available</p>}
          </div>
        </ChartWithDrillDown>

        {/* Account Executive Performance */}
        <ChartWithDrillDown
          title="Account Executive Performance"
          subtitle="Individual performance metrics and proposal status"
          data={aeData}
          columns={aeColumns}
        >
          <div className="space-y-3">
            {Object.entries(account_executive_breakdown).sort(([, a], [, b]) => b.proposals - a.proposals).slice(0, 5).map(([name, data]) => (
              <div key={name} className="border-l-4 border-indigo-500 pl-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">{name}</span>
                  <span className="text-xs text-slate-500">{data.proposals} proposals</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-600">
                  <span>{data.approved_proposals} approved</span>
                  <span className="text-green-600 font-semibold">${(data.total_value / 1000).toFixed(1)}K</span>
                </div>
              </div>
            ))}
            {Object.keys(account_executive_breakdown).length === 0 && <p className="text-sm text-slate-500 text-center py-4">No account executive data available</p>}
          </div>
        </ChartWithDrillDown>

        {/* State Distribution */}
        <ChartWithDrillDown
          title="Geographic Distribution"
          subtitle="Proposal distribution across states"
          data={stateData}
          columns={stateColumns}
        >
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(state_breakdown).sort(([, a], [, b]) => b - a).slice(0, 10).map(([state, count]) => (
              <div key={state} className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                <span className="text-sm font-medium text-slate-700">{state}</span>
                <span className="text-sm text-slate-600">{count}</span>
              </div>
            ))}
            {Object.keys(state_breakdown).length === 0 && <p className="text-sm text-slate-500 text-center py-4 col-span-2">No geographic data available</p>}
          </div>
        </ChartWithDrillDown>
      </div>

      {/* Revenue Distribution Donut Charts */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-indigo-600" />
              Revenue Distribution by Service
            </h3>
            <p className="text-sm text-slate-600 mt-1">Visual breakdown of revenue across services</p>
          </div>
          <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
            <button onClick={() => setDonutChartView('gross')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${donutChartView === 'gross' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Gross Revenue</button>
            <button onClick={() => setDonutChartView('net')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${donutChartView === 'net' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Net Revenue</button>
          </div>
        </div>

        {donutChartData.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={donutChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`} labelLine={false}>
                    {donutChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(value) => `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-slate-700">{donutChartView === 'gross' ? 'Gross Revenue' : 'Net Revenue'} Breakdown</h4>
                <span className="text-xs text-slate-500">Total: ${donutChartData.reduce((sum, item) => sum + item.value, 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              </div>
              {donutChartData.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }} />
                    <span className="text-sm font-medium text-slate-900">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-slate-900">${item.value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                    <div className="text-xs text-slate-500">{((item.value / donutChartData.reduce((sum, d) => sum + d.value, 0)) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-500">
            <PieChartIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No revenue data available for chart</p>
            <p className="text-xs mt-1">Adjust filters to see revenue distribution</p>
          </div>
        )}
      </div>
    </>
  );
}
